import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  CheckCircle2
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  isToday,
  startOfWeek,
  endOfWeek
} from 'date-fns';
import { SocialPost, SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';

interface PostCalendarProps {
  posts: SocialPost[];
  onDayClick?: (date: Date) => void;
  onPostClick?: (post: SocialPost) => void;
  onCreatePost?: (date: Date) => void;
}

export const PostCalendar: React.FC<PostCalendarProps> = ({
  posts,
  onDayClick,
  onPostClick,
  onCreatePost
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  // Get posts grouped by date
  const postsByDate = useMemo(() => {
    const grouped: Record<string, SocialPost[]> = {};

    posts.forEach(post => {
      const date = post.scheduled_for || post.published_at || post.created_at;
      if (date) {
        const dateKey = format(new Date(date), 'yyyy-MM-dd');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(post);
      }
    });

    return grouped;
  }, [posts]);

  // Calculate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev =>
      direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
    );
  };

  const renderDayContent = (day: Date, dayPosts: SocialPost[]) => {
    const scheduledPosts = dayPosts.filter(p => p.status === 'scheduled');
    const publishedPosts = dayPosts.filter(p => p.status === 'published');

    if (dayPosts.length === 0) {
      return null;
    }

    if (dayPosts.length <= 2) {
      return (
        <div className="space-y-0.5 mt-1">
          {dayPosts.slice(0, 2).map((post) => (
            <button
              key={post.id}
              onClick={(e) => {
                e.stopPropagation();
                onPostClick?.(post);
              }}
              className={cn(
                "w-full text-left text-[10px] px-1 py-0.5 rounded truncate transition-colors",
                post.status === 'published'
                  ? "bg-green-100 text-green-800 hover:bg-green-200"
                  : "bg-blue-100 text-blue-800 hover:bg-blue-200"
              )}
            >
              <span className="line-clamp-1">{post.content.slice(0, 20)}...</span>
            </button>
          ))}
        </div>
      );
    }

    // More than 2 posts - show summary
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="w-full mt-1 text-[10px] px-1 py-0.5 bg-gray-100 hover:bg-gray-200 rounded text-center"
          >
            {dayPosts.length} posts
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <div className="space-y-2">
            <p className="font-medium text-sm">{format(day, 'EEEE, MMMM d')}</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {dayPosts.map((post) => (
                <button
                  key={post.id}
                  onClick={() => onPostClick?.(post)}
                  className={cn(
                    "w-full text-left p-2 rounded text-xs transition-colors",
                    post.status === 'published'
                      ? "bg-green-50 hover:bg-green-100"
                      : "bg-blue-50 hover:bg-blue-100"
                  )}
                >
                  <div className="flex items-center gap-1 mb-1">
                    {post.target_platforms.slice(0, 3).map((platform) => {
                      const Icon = getPlatformIcon(platform);
                      return (
                        <Icon
                          key={platform}
                          className="h-3 w-3"
                          style={{ color: PLATFORM_INFO[platform].color }}
                        />
                      );
                    })}
                    {post.status === 'published' ? (
                      <CheckCircle2 className="h-3 w-3 text-green-600 ml-auto" />
                    ) : (
                      <Clock className="h-3 w-3 text-blue-600 ml-auto" />
                    )}
                  </div>
                  <p className="line-clamp-2">{post.content}</p>
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Content Calendar</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[120px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Legend */}
        <div className="flex gap-4 mb-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" />
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
            <span>Published</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="border rounded-lg overflow-hidden">
          {/* Week day headers */}
          <div className="grid grid-cols-7 bg-muted/50">
            {weekDays.map((day) => (
              <div
                key={day}
                className="py-2 text-center text-xs font-medium text-muted-foreground border-b"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayPosts = postsByDate[dateKey] || [];
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isTodayDate = isToday(day);

              return (
                <div
                  key={index}
                  onClick={() => onDayClick?.(day)}
                  className={cn(
                    "min-h-[80px] p-1 border-b border-r cursor-pointer transition-colors",
                    !isCurrentMonth && "bg-muted/30 text-muted-foreground",
                    isTodayDate && "bg-primary/5",
                    "hover:bg-accent/50",
                    index % 7 === 6 && "border-r-0", // Last column
                    index >= calendarDays.length - 7 && "border-b-0" // Last row
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        "text-sm font-medium h-6 w-6 flex items-center justify-center rounded-full",
                        isTodayDate && "bg-primary text-primary-foreground"
                      )}
                    >
                      {format(day, 'd')}
                    </span>
                    {isCurrentMonth && onCreatePost && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCreatePost(day);
                        }}
                        className="opacity-0 group-hover:opacity-100 hover:opacity-100 h-5 w-5 flex items-center justify-center rounded hover:bg-primary/10 transition-opacity"
                      >
                        <Plus className="h-3 w-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {renderDayContent(day, dayPosts)}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostCalendar;
